'use client';

import React, { useRef, useEffect } from 'react';
import { asLinkAttrs, isFilled, type ImageField, type LinkField, type RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import CustomRichText from '../../blog/components/CustomRichText';
import SplashButton from '@/components/SplashButton';
import { ChevronDown } from 'lucide-react';

export interface IndustryHeroAward {
  title?: RichTextField;
  subtitle?: RichTextField;
  image?: ImageField;
}

interface IndustryHeroProps {
  title: RichTextField;
  description: RichTextField;
  id: string;
  primaryCta: LinkField;
  primaryFallbackLabel: string;
  secondaryCta?: LinkField;
  award?: IndustryHeroAward;
  logosLabel?: string;
  logos?: ImageField[];
}

// Up to this many logos sit in a static row; more than that become a seamless marquee.
const HERO_LOGO_GRID_MAX = 6;
// Logo files are solid black (Logos library); force white on the dark band.
const HERO_LOGO_CLASS =
  'h-16 w-full object-contain opacity-[0.85] brightness-0 invert transition-opacity hover:opacity-100 md:h-20';

// Prismic web links are stored as absolute site URLs; strip our own origin so
// they navigate client-side instead of as external links.
const SITE_ORIGIN = /^https?:\/\/hk\.psglobalconsulting\.com(?=\/|$)/;
const toInternal = (href: string) => href.replace(SITE_ORIGIN, '') || '/';

// Ghost text link (“See how we work with distributors”), centred on mobile. Only the label comes
// from Prismic: it always scrolls to the section directly below the hero.
function ScrollCta({ label, targetRef }: { label: string; targetRef: React.RefObject<HTMLElement | null> }) {
  const onClick = () => {
    const el = targetRef.current;
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().bottom + window.scrollY, behavior: 'smooth' });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex min-h-[50px] w-full items-center justify-center gap-1.5 px-2 py-4 text-center text-sm font-medium text-white transition-colors hover:text-blue-200 sm:w-auto sm:justify-start sm:text-left"
    >
      {label}
      <ChevronDown aria-hidden className="h-4 w-4 shrink-0 transition-transform group-hover:translate-y-0.5" />
    </button>
  );
}

// Frosted award card. Defaults to the site's white Partner-of-the-Year mark when
// no Prismic badge image is set.
function AwardCard({ title, subtitle, image }: IndustryHeroAward) {
  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);
  if (!hasTitle && !hasSubtitle && !isFilled.image(image)) return null;

  return (
    <aside
      aria-label="Award"
      className="flex w-full max-w-[420px] flex-col items-center gap-4 rounded-[20px] border border-white/[0.14] bg-white/[0.07] p-6 text-center shadow-[0_30px_60px_-30px_rgba(0,0,0,0.7)] backdrop-blur-[14px] sm:flex-row sm:text-left xl:w-[300px] xl:flex-col xl:justify-self-end xl:px-[26px] xl:pb-[26px] xl:pt-7 xl:text-center"
    >
      <div className="flex h-24 w-full max-w-[220px] shrink-0 items-center justify-center sm:w-40 xl:h-28 xl:w-full">
        {isFilled.image(image) ? (
          <PrismicNextImage field={image} className="max-h-full w-auto object-contain" />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src="/award2.png" alt="Oracle NetSuite Partner of the Year" className="h-auto w-full object-contain" />
        )}
      </div>
      <div className="flex min-w-0 flex-col items-center gap-3 sm:items-start xl:items-center">
        {hasTitle ? (
          <div className="text-[17px] font-medium leading-[1.35] tracking-[-0.01em] text-white/85 xl:text-[19px]">
            <PrismicRichText
              field={title}
              components={{
                paragraph: ({ children }) => <h3>{children}</h3>,
                strong: ({ children }) => <strong className="block font-semibold text-white">{children}</strong>,
              }}
            />
          </div>
        ) : null}
        <div className="hidden h-0.5 w-9 rounded-sm bg-[#B08A2E] xl:block" />
        {hasSubtitle ? (
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#E2C375]">
            <PrismicRichText field={subtitle} components={{ paragraph: ({ children }) => <p>{children}</p> }} />
          </div>
        ) : null}
      </div>
    </aside>
  );
}

const IndustryHero: React.FC<IndustryHeroProps> = ({
  title,
  description,
  id,
  primaryCta,
  primaryFallbackLabel,
  secondaryCta,
  award,
  logosLabel,
  logos = [],
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Autoplay is only allowed while muted; set it imperatively too (the
    // attribute alone is unreliable after a client-side navigation).
    video.muted = true;

    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    // Already buffered (hard load / cached): just play — don't call load(),
    // which would needlessly re-fetch and could stutter the working case.
    if (video.readyState >= 2) {
      tryPlay();
      return;
    }

    // Client-side navigation on mobile: the element often mounts before the
    // source starts loading (iOS treats preload="auto" as "metadata"), so the
    // 'canplay' event may never fire and the frame stays black. Force the load
    // and play as soon as any data is available.
    const onReady = () => tryPlay();
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
    try {
      video.load();
    } catch {
      /* no-op */
    }
    return () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
    };
  }, [id]);

  const { href: rawPrimaryHref } = asLinkAttrs(primaryCta);
  const primaryHref = rawPrimaryHref ? toInternal(rawPrimaryHref) : undefined;
  const filledLogos = logos.filter((logo) => isFilled.image(logo));

  return (
    // Fill the viewport below the 40px AnnouncementBar (components/AnnouncementBar.tsx) so the
    // logo band sits exactly on the fold. dvh, not vh, so mobile browser chrome doesn't crop it.
    <section ref={sectionRef} className="relative isolate flex min-h-[calc(100dvh-40px)] flex-col overflow-hidden bg-[#0b1419] text-white">
      {/* Poster as a guaranteed base layer: a plain <img> always fetches on
          client-side navigation, so the hero is never black while the video
          (which can be slow or blocked to autoplay on mobile) catches up. The
          video sits on top and covers the image once it starts playing. */}
      <div className="absolute inset-0 -z-20">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`/video/${id}.jpg`}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <video
          key={id}
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={`/video/${id}.mp4`}
          poster={`/video/${id}.jpg`}
          preload="auto"
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(8,20,24,0.78)_0%,rgba(8,20,24,0.55)_45%,rgba(8,20,24,0.92)_100%)]" />
      {/* Left-to-right scrim so the copy reads over busy footage; clear by ~60% so the video stays visible on the right. */}
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(8,20,24,0.55)_0%,rgba(8,20,24,0.3)_35%,rgba(8,20,24,0)_60%)]" />

      {/* Top padding clears the overlaid site header + TinyPageTitle breadcrumb. */}
      <div className="container mx-auto grid flex-1 grid-cols-1 items-center gap-10 pb-16 pt-[150px] sm:pt-[200px] xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-16">
        <div>
          <h2 className="max-w-[820px] text-pretty text-4xl font-medium leading-[1.08] tracking-[-0.02em] lg:text-5xl xl:text-[64px]">
            <CustomRichText
              field={title}
              components={{
                paragraph: ({ children }) => <>{children}</>,
                strong: ({ children }) => <strong className="font-medium text-blue-200">{children}</strong>,
              }}
            />
          </h2>
          <div className="mt-6 max-w-[680px] text-pretty text-base leading-[1.65] text-white/[0.88] md:text-[19px]">
            <CustomRichText field={description} />
          </div>
          <div className="mt-9 flex flex-wrap items-center gap-4 sm:gap-x-6">
            {primaryHref ? (
              <SplashButton
                url={primaryHref}
                text={primaryCta.text || primaryFallbackLabel}
                color="blue"
                size="md"
                className="w-full sm:w-auto sm:min-w-[200px]"
              />
            ) : null}
            {secondaryCta?.text ? <ScrollCta label={secondaryCta.text} targetRef={sectionRef} /> : null}
          </div>
        </div>
        {award ? <AwardCard {...award} /> : null}
      </div>

      {filledLogos.length > 0 ? (
        <div className="border-t border-white/[0.14] bg-[rgba(6,14,18,0.35)] backdrop-blur-[4px]">
          <div className="container mx-auto flex flex-col items-center gap-5 py-4 md:flex-row md:gap-10">
            {logosLabel ? (
              <div className="flex-none text-balance text-center text-[11px] font-semibold uppercase leading-normal tracking-[0.2em] text-white/60 md:max-w-[150px] md:border-r md:border-white/[0.14] md:pr-10 md:text-left">
                {logosLabel}
              </div>
            ) : null}
            {filledLogos.length > HERO_LOGO_GRID_MAX ? (
              <div className="hero-logo-marquee relative w-full min-w-0 flex-1 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_6%,black_94%,transparent)]">
                <div
                  className="hero-logo-track flex w-max items-center"
                  style={{ '--hero-logo-duration': `${filledLogos.length * 4}s` } as React.CSSProperties}
                >
                  {[...filledLogos, ...filledLogos].map((logo, i) => (
                    <div
                      key={i}
                      aria-hidden={i >= filledLogos.length || undefined}
                      className="w-[150px] shrink-0 px-4 md:w-[200px] md:px-6"
                    >
                      <PrismicNextImage field={logo} className={HERO_LOGO_CLASS} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid w-full flex-1 grid-cols-3 items-center gap-6 md:grid-cols-6 md:gap-9">
                {filledLogos.map((logo, i) => (
                  <PrismicNextImage key={i} field={logo} className={HERO_LOGO_CLASS} />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default IndustryHero;
